import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {
  Autocomplete, GoogleMap, LoadScript, Marker,
} from '@react-google-maps/api';

import { useField } from 'formik';

import {
  selectComboBoxOptions,
} from '@redux/selectors/FormSelector';

import usePrevious from '@hooks/usePrevious';

const defaultCenter = {
  lat: 53.54288713007532,
  lng: -113.49615377351157,
};

const containerStyles = {
  width: '100%',
  height: '60vh',
};

const defaultLibraries = ['places', 'geometry'];

const LocationPlotterMap = ({ onCoordsClicked }) => {
  const [autocompleteInstance, setAutocompleteInstance] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [zoom, setZoom] = useState(5);

  const [zoomToLocation, setZoomToLocation] = useState(false);

  const [latValue, setLatValue] = useState(null);
  const [lngValue, setLngValue] = useState(null);

  const [latField] = useField('Latitude');
  const [lngField] = useField('Longitude');

  const [streetNoField] = useField('StreetNumber');
  const [streetNameField] = useField('StreetName');
  const [postalCodeField] = useField('PostalCode');
  const [streetSuffixField] = useField('StreetSuffix'); // ComboBox
  const [streetDirSuffixField] = useField('StreetDirSuffix'); // ComboBox
  const [cityField] = useField('City'); // ComboBox
  const [countyField] = useField('CountyOrParish'); // ComboBox
  const [provinceField] = useField('StateOrProvince'); // ComboBox

  const streetSuffixOptions = useSelector(state => selectComboBoxOptions(state, 'StreetSuffix'));
  const streetDirSuffixOptions = useSelector(state => selectComboBoxOptions(state, 'StreetDirSuffix'));
  const cityOptions = useSelector(state => selectComboBoxOptions(state, 'City'));
  const countyOptions = useSelector(state => selectComboBoxOptions(state, 'CountyOrParish'));
  const provinceOptions = useSelector(state => selectComboBoxOptions(state, 'StateOrProvince'));

  const streetNo = useMemo(() => streetNoField.value ?? '', [streetNoField]);
  const streetName = useMemo(() => streetNameField.value ?? '', [streetNameField]);
  const postalCode = useMemo(() => postalCodeField.value ?? '', [postalCodeField]);
  const streetSuffix = useMemo(() => streetSuffixOptions.find(opt => opt.value === streetSuffixField.value)?.label ?? '', [streetSuffixField.value, streetSuffixOptions]);
  const streetDirSuffix = useMemo(() => streetDirSuffixOptions.find(opt => opt.value === streetDirSuffixField.value)?.label ?? '', [streetDirSuffixField.value, streetSuffixOptions]);
  const city = useMemo(() => cityOptions.find(opt => opt.value === cityField.value)?.label ?? '', [cityField.value, streetSuffixOptions]);
  const county = useMemo(() => countyOptions.find(opt => opt.value === countyField.value)?.label ?? '', [countyField.value, streetSuffixOptions]);
  const province = useMemo(() => provinceOptions.find(opt => opt.value === provinceField.value)?.value ?? '', [provinceField.value, streetSuffixOptions]);

  const [addressInputValue, setAddressInputValue] = useState('');

  const [formAddress, setFormAddress] = useState('');
  useEffect(() => {
    const cityOrCounty = city || county;
    const address = `${streetNo} ${streetName} ${streetSuffix} ${streetDirSuffix}, ${cityOrCounty}, ${province} ${postalCode}`
      .replace(/\s+,/g, ',') // moves any spaced comma next to the leftmost address token
      .trim() // cleanups any leading and trailing white space
      .replace(/^,+|,+$/g, '') // cleanups leading and trailing commas
      .replace(/,+/g, ',') // ensures only one comma is used between address tokens
      .replace(/\s+/g, ' ') // ensures only one space is used between address tokens
      .trim(); // cleanups any leading and trailing white space
    setFormAddress(address);
    setAddressInputValue(address);
  }, [streetNo, streetName, streetSuffix, streetDirSuffix, city, county, province, postalCode]);

  const [isModalShown, setIsModalShown] = useState(false);
  const previousIsModalShown = usePrevious(isModalShown);
  const onModalShow = () => setIsModalShown(true);
  const onModalHide = () => setIsModalShown(false);

  useEffect(() => {
    window.jQuery('#locationPlotterModal').on('show.bs.modal', onModalShow);
    window.jQuery('#locationPlotterModal').on('hide.bs.modal', onModalHide);
    return () => {
      window.jQuery('#locationPlotterModal').off('show.bs.modal', onModalShow);
      window.jQuery('#locationPlotterModal').off('hide.bs.modal', onModalHide);
    };
  }, []);

  useEffect(() => {
    if (previousIsModalShown && !isModalShown && !addressInputValue) {
      // Populate address field with existing form address when exiting modal without an address.
      setAddressInputValue(formAddress);
    }
  }, [isModalShown]);

  useEffect(() => {
    if (latField.value !== '') {
      const parseLat = parseFloat(latField.value);

      if (!latValue || parseLat !== latValue) {
        setLatValue(parseLat);
      }
      setZoomToLocation(true);
    } else if (latValue) {
      setLatValue(null); // If the user clears the lng coordinate we remove it from the map
    }
  }, [latField.value]);

  useEffect(() => {
    if (lngField.value !== '') {
      const parseLng = parseFloat(lngField.value);

      if (!lngValue || parseLng !== lngValue) {
        setLngValue(parseLng);
      }
      setZoomToLocation(true);
    } else if (lngValue) {
      setLngValue(null); // If the user clears the lng coordinate we remove it from the map
    }
  }, [lngField.value]);

  useEffect(() => {
    if (mapInstance && latValue && lngValue) {
      mapInstance.panTo({ lat: latValue, lng: lngValue });

      if (zoomToLocation) {
        mapInstance.setZoom(18);
        setZoom(18);
      }
    }
  }, [mapInstance, latValue, lngValue, zoomToLocation]);

  const onPlaceChanged = () => {
    if (autocompleteInstance) {
      const place = autocompleteInstance.getPlace();

      if (!place.geometry) return;
      if (mapInstance) {
        if (place.geometry.viewport) {
          mapInstance.fitBounds(place.geometry.viewport);
        } else {
          mapInstance.setCenter(place.geometry.location);
          mapInstance.setZoom(17);
        }
      }
      setLatValue(place.geometry.location.lat());
      setLngValue(place.geometry.location.lng());
      onCoordsClicked({
        Latitude: place.geometry.location.lat().toString(),
        Longitude: place.geometry.location.lng().toString(),
      });
      setAddressInputValue(place.formatted_address);
    }
  };

  const onMapClick = e => {
    if (mapInstance) {
      setZoomToLocation(false);
      setLatValue(e.latLng.lat());
      setLngValue(e.latLng.lng());
      onCoordsClicked({
        Latitude: e.latLng.lat().toString(),
        Longitude: e.latLng.lng().toString(),
      });
    }
  };

  const onMapLoad = useCallback(map => {
    setMapInstance(map);
    const parcelLayer = new window.google.maps.ImageMapType({
      getTileUrl: (c, z) => `${process.env.REACT_APP_MAPS_PARCEL_LAYER}${z}/${c.x}/${c.y}.png`,
      maxZoom: 20,
      minZoom: 16,
      tileSize: new window.google.maps.Size(256, 256),
      name: 'Parcels',
    });

    const neighbourhoodLayer = new window.google.maps.ImageMapType({
      getTileUrl: (c, z) => `${process.env.REACT_APP_MAPS_NEIGHBOURHOOD_LAYER}${z}/${c.x}/${c.y}.png`,
      maxZoom: 15,
      minZoom: 12,
      tileSize: new window.google.maps.Size(256, 256),
      name: 'Neighbourhoods',
    });

    const municipalityLayer = new window.google.maps.ImageMapType({
      getTileUrl: (c, z) => `${process.env.REACT_APP_MAPS_MUNICIPALITY_LAYER}${z}/${c.x}/${c.y}.png`,
      maxZoom: 13,
      minZoom: 7,
      tileSize: new window.google.maps.Size(256, 256),
      name: 'Municipalities',
    });
    map.overlayMapTypes.insertAt(0, parcelLayer);
    map.overlayMapTypes.insertAt(1, neighbourhoodLayer);
    map.overlayMapTypes.insertAt(2, municipalityLayer);
  }, []);

  const onMapUnmount = () => {
    setMapInstance(null);
  };

  const onAutoCompleteLoad = autocomplete => {
    setAutocompleteInstance(autocomplete);
  };

  const onAutoCompleteUnload = () => {
    setAutocompleteInstance(null);
  };

  const handleClear = () => {
    document.getElementById('locationPlotter').value = '';
  };

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
      libraries={defaultLibraries}
    >
      <div className="form-group">
        <Autocomplete
          onLoad={onAutoCompleteLoad}
          onPlaceChanged={onPlaceChanged}
          onUnmount={onAutoCompleteUnload}
          restrictions={{ country: ['us', 'ca'] }}
        >
          <div className="input-group mb-3">
            <input
              className="form-control pac-target-input"
              id="locationPlotter"
              placeholder="Search by address"
              onChange={e => setAddressInputValue(e.value)}
              value={addressInputValue}
            />
            <div className="input-group-append">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="clear-button">Clear</Tooltip>}
              >
                <button className="btn btn-primary" type="button" onClick={handleClear}>
                  <i className="far fa-times" />
                </button>
              </OverlayTrigger>
            </div>
          </div>
        </Autocomplete>
      </div>
      <GoogleMap
        center={defaultCenter}
        id="LocationPlotterMap"
        mapContainerStyle={containerStyles}
        onClick={onMapClick}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        zoom={zoom}
        options={{ minZoom: 5, maxZoom: 20 }}
      >
        {
          latValue && lngValue
          && (
            <Marker
              position={{ lat: latValue, lng: lngValue }}
            />
          )
        }
      </GoogleMap>
    </LoadScript>
  );
};

LocationPlotterMap.propTypes = {
  onCoordsClicked: PropTypes.func.isRequired,
};

export default LocationPlotterMap;
